import { isNil, size } from 'lodash';
import { divIcon, marker } from 'leaflet';
import moment from 'moment';
import { setOrders, setSolutionJson } from 'actions';
import {
  isActivityToDisplay,
  isPointDepot,
  isPointReturnLocation,
  isRouteToDisplay,
  isUnassignedJob,
  isUndesiredPoint,
} from './MapFeatureFilters';
import { isMobileDevice } from '../helpers';
import { getSafeValue, setSafeValue } from '../security';
import {
  createDepotSvg,
  createMarkerSvg,
  createOrderUnassignedSvg,
  createTourUnselectedSvg,
  createUndesiredPointSvg,
} from '../../icons/marker';

const POSITION_TYPES = {
  FIRST: 'first',
  LAST: 'last',
};

const attachPopupHoverBehavior = (markerInstance, shouldOpenOnHover = true) => {
  let closeTimeout;

  markerInstance.on('mouseover', () => {
    if (shouldOpenOnHover) {
      clearTimeout(closeTimeout);
      markerInstance.openPopup();
      setTimeout(() => {
        const popup = markerInstance.getPopup();
        if (popup && popup._container) {
          popup._container.addEventListener('mouseenter', () => clearTimeout(closeTimeout));
          popup._container.addEventListener('mouseleave', () => markerInstance.closePopup());
        }
      }, 100);
    }
  });

  markerInstance.on('mouseout', () => {
    closeTimeout = setTimeout(() => markerInstance.closePopup(), 100);
  });
};

export const getTagsRow = (jobTags) =>
  size(jobTags) > 0
    ? `<tr>
    <td>Tags:</td>
    <td>${jobTags && jobTags.join(', ')}</td>
  </tr>`
    : '';

export const getTimeWindowRows = (tw, offset, hoveredId) => {
  return (tw || [])
    .map((window) =>
      window.map((t) => {
        const id = t[2];
        if (id === hoveredId) {
          const firstDate = moment(t[0]).utc().utcOffset(offset).format('lll');
          const secondDate = moment(t[1]).utc().utcOffset(offset).format('lll');
          return `<tr><td>TW:</td><td>${firstDate} - ${secondDate}</td></tr>`;
        }
        return null;
      }),
    )
    .join('');
};

export const createActivityTable = (
  duration,
  group,
  recharge,
  tw,
  hourOffset,
  showTWs,
  hoveredId,
  loadRow,
  position,
  terr,
  categories,
  houseKey,
  skills,
  maxTime,
  priority,
  parking,
) => {
  return `
  ${duration}
  ${group}
  ${recharge}
  ${showTWs && getTimeWindowRows(tw, hourOffset, hoveredId)}
  ${loadRow}
  ${position}
  ${terr}
  ${skills}
  ${categories}
  ${houseKey}
  ${maxTime}
  ${priority}
  ${parking}
  `;
};

export const createStopTable = (
  group,
  skills,
  categories,
  position,
  priority,
  duration,
  load,
  recharge,
  order,
  parking,
) => {
  return `
    ${group}
    ${skills}
    ${categories}
    ${position}
    ${priority}
    ${duration}
    ${load}
    ${recharge}
    ${order}
    ${parking}`;
};

export const getTitle = (
  translations,
  labels = undefined,
  typesObj = {},
  highPriority = false,
  address = undefined,
) => {
  const {
    wizard: {
      orders: { editedOrderPriorityHigh },
    },
    global: {
      breakLabel,
      reloadLabel,
      rechargeLabel,
      departureLabel,
      arrivalLabel,
      workingRestTimeLabel,
      drivingRestTimeLabel,
      dutyWorkingRestTimeLabel,
      dutyDrivingRestTimeLabel,
      pudoLabel,
    },
  } = translations;

  if (Object.keys(typesObj).length === 1) {
    if (typesObj.break) return `<div class="labels">${breakLabel}<br/></div>`;
    if (typesObj.recharge) return `<div class="labels">${rechargeLabel}<br/></div>`;
    if (typesObj.pudo) return `<div class="labels">${pudoLabel}<br/></div>`;
    if (typesObj.workingRestTime) return `<div class="labels">${workingRestTimeLabel}<br/></div>`;
    if (typesObj.dutyWorkingRestTime)
      return `<div class="labels">${dutyWorkingRestTimeLabel}<br/></div>`;
    if (typesObj.dutyDrivingRestTimeLabel)
      return `<div class="labels">${dutyDrivingRestTimeLabel}<br/></div>`;
    if (typesObj.drivingRestTime) return `<div class="labels">${drivingRestTimeLabel}<br/></div>`;
    if (typesObj.reload) return `<div class="labels">${reloadLabel}<br/></div>`;
    if (typesObj.departure) return `<div class="labels">${departureLabel}<br/></div>`;
    if (typesObj.arrival) return `<div class="labels">${arrivalLabel}<br/></div>`;
  }
  if (labels && highPriority && address)
    return `<div class="labels"><div class="high-priority">${editedOrderPriorityHigh}</div>${address}<br/>${labels.join(
      '<br />',
    )}</div>`;
  if (labels) return `<div class="labels">${labels.join('<br />')}</div>`;
  return '';
};

export const createTablePopup = (
  translations,
  title,
  jobTags,
  arrival,
  departure,
  territoryName,
  groupName,
  tw,
  hourOffset,
  skills = undefined,
  categories = undefined,
  position = undefined,
  priority = undefined,
  durationRow = undefined,
  demand = undefined,
  rechargeDistance = undefined,
  jobOrder,
  isPUDO,
  isSolution,
  isStopMarker,
  showDurationAndGroup,
  showTWs,
  hoveredId,
  stopTerritory,
  houseKeyId,
  maxTime,
  parkingDurationElem,
) => {
  const skillsRow =
    !isStopMarker && skills && skills.length > 0
      ? `<tr><td>${translations.skills}:</td><td>${skills?.join(', ')}</td></tr>`
      : '';
  const categoriesRow =
    !isStopMarker && categories && categories.length > 0
      ? `<tr><td>${translations.categories}:</td><td>${categories.join(', ')}</td></tr>`
      : '';
  const positionRow =
    !isStopMarker && position && position.length > 0
      ? `<tr><td>${translations.position}:</td><td>${position.join(', ')}</td></tr>`
      : '';
  const priorityRow =
    !isPUDO && priority && priority.length > 0
      ? `<tr><td>${translations.priority}:</td><td>${priority.join(', ')}</td></tr>`
      : '';
  const loadRow =
    !isPUDO && !rechargeDistance && demand && demand.length > 0
      ? `<tr><td>${isStopMarker ? translations.load : translations.demand}:</td><td>${demand.join(
          ', ',
        )}</td></tr>`
      : '';
  const rechargeRow =
    rechargeDistance && rechargeDistance > 0
      ? `<tr><td>${translations.recharge}:</td><td>${rechargeDistance} m</td></tr>`
      : '';
  const orderRow =
    !isStopMarker && jobOrder
      ? `<tr><td>${translations.jobOrder}:</td><td>${jobOrder}</td></tr>`
      : '';
  const findHouseKey =
    houseKeyId &&
    houseKeyId.map((keyArr) => keyArr[1] === hoveredId && keyArr[0]).filter((val) => val !== false);
  const houseKeyRow =
    findHouseKey && findHouseKey.length > 0
      ? `<tr><td>${translations.houseKey}:</td><td>${findHouseKey[0]}</td></tr>`
      : '';
  const findMaxTime =
    maxTime &&
    maxTime.map((keyArr) => keyArr[1] === hoveredId && keyArr[0]).filter((val) => val !== false);
  const maxTimeRow =
    findMaxTime && findMaxTime.length > 0
      ? `<tr><td>${translations.maxTime}:</td><td>${findMaxTime[0]}</td></tr>`
      : '';
  const parkingRow =
    parkingDurationElem && parkingDurationElem.length > 0 ? parkingDurationElem : '';

  const filteredTags = jobTags.filter((tag) => !isNil(tag));
  return `${title}
    <table>${!isStopMarker ? getTagsRow(filteredTags) : ''}
    <tr>
      <td>${translations.arrivalJobMarkerTrans}:&nbsp;&nbsp;</td>
      <td>${moment(arrival).utc().utcOffset(hourOffset).format('lll')}</td>
    </tr>
    <tr>
      <td>${translations.departureJobMarkerTrans}:&nbsp;&nbsp;</td>
      <td>${moment(departure).utc().utcOffset(hourOffset).format('lll')}</td>
    </tr>
    ${
      showDurationAndGroup || showTWs
        ? createActivityTable(
            durationRow,
            groupName,
            rechargeRow,
            tw,
            hourOffset,
            showTWs,
            hoveredId,
            loadRow,
            positionRow,
            territoryName || stopTerritory,
            categoriesRow,
            houseKeyRow,
            skillsRow,
            maxTimeRow,
            priorityRow,
            parkingRow,
          )
        : createStopTable(
            groupName,
            skillsRow,
            categoriesRow,
            positionRow,
            priorityRow,
            durationRow,
            loadRow,
            rechargeRow,
            orderRow,
            parkingRow,
          )
    }
    </table>
`;
};

export const createUndesiredPopup = (count) => {
  return `
    <table><b>Intersection Info</b>
    <tr>
      <td>${`Count: ${count}`}</td>
    </tr>
    </table>
`;
};

export function getTerritoryNameByOrderId(data, id) {
  if (!Array.isArray(data)) return null;

  const order = data.find((ord) => ord.InternalID === id);
  return order ? order.Territories?.[0] || null : data.name;
}

export const createPlanPopup = (
  title,
  typeString,
  territoryMarkerTrans,
  groupName,
  el,
  territory,
  skills,
  skillsMarkerTrans,
  priority,
  priorityTrans,
  ords,
) => {
  const territoryFull = getTerritoryNameByOrderId(ords, el.feature.properties.id);
  const territoryName =
    territoryFull && territory?.type !== 'group'
      ? `<div>${territoryMarkerTrans}: ${territoryFull}</div>`
      : '';
  const skillsArr = skills ? `<div>${skillsMarkerTrans}: ${skills}</div>` : '';
  const priorityFull = priority ? `<div>${priorityTrans}: ${priority}</div>` : '';

  return `${title}${typeString}${territoryName || ''}${groupName}${skillsArr}${priorityFull}`;
};

export const makeDepotMarker = (
  feature,
  latlng,
  onMarkerClick,
  translations,
  onMarkerDragEnd,
  isSolution,
  highlight,
  asyncOn,
) => {
  const {
    map: { depotMarkerTrans },
  } = translations;
  const hover = true;
  const address =
    feature.properties && feature.properties.address ? `${feature.properties.address}<br/>` : '';
  const labels = feature.properties && feature.properties.labels ? feature.properties.labels : [];
  const title =
    (labels.length !== 0 && labels[0] !== '\n') || address.length !== 0
      ? `<div class="labels">${address}${labels.join('<br />')}</div>`
      : '';

  const options = {
    className: 'leaflet-marker-icon-job depot-icon',
    html: L.Util.template(createDepotSvg(highlight)),
    iconSize: [64, 64],
    iconAnchor: [32, 64],
  };

  const m = marker(latlng, {
    icon: divIcon(options),
    draggable: !isSolution && !asyncOn,
    zIndexOffset: -1000,
  }).bindPopup(`${title}${depotMarkerTrans}`, {
    className: 'custom-depot',
    autoPan: true,
    autoPanPadding: [50, 50],
    keepInView: true,
    maxHeight: 300,
  });

  attachPopupHoverBehavior(m, hover);

  m.on('click', () => {
    if (isMobileDevice()) m.openPopup();
    else onMarkerClick(feature);
  });
  m.on('dragend', () => {
    onMarkerDragEnd(m, feature);
  });
  return m;
};

export const makeReturnLocationMarker = (
  feature,
  latlng,
  onMarkerClick,
  translations,
  onMarkerDragEnd,
  isSolution,
  asyncOn,
) => {
  const {
    map: { returnLocationMarkerTrans },
  } = translations;
  const address =
    feature.properties && feature.properties.address ? `${feature.properties.address}<br/>` : '';
  const title = `<div class="labels">${address}</div>`;

  const m = marker(latlng, {
    icon: divIcon({
      className: `leaflet-marker-icon-job return-location-icon`,
      html: '<div></div>',
      iconSize: [64, 64],
      iconAnchor: [32, 64],
    }),
    draggable: !isSolution && !asyncOn,
  }).bindPopup(`${title}${returnLocationMarkerTrans}`, {
    className: 'custom-depot',
    autoPan: true,
    autoPanPadding: [50, 50],
    keepInView: true,
    maxHeight: 300,
  });

  attachPopupHoverBehavior(m);

  m.on('click', () => {
    if (isMobileDevice()) m.openPopup();
    else onMarkerClick(feature);
  });
  m.on('dragend', () => {
    onMarkerDragEnd(m, feature);
  });
  return m;
};

const getColor = (typesObj, jobMarkerColor, highlight, oneType, position = []) => {
  const colorMap = {
    pickup: jobMarkerColor.pickup,
    pudo: position.includes(POSITION_TYPES.FIRST) ? jobMarkerColor.pickup : jobMarkerColor.delivery,
  };

  const defaultColor = { inner: 'grey', outer: 'grey', text: 'white' };
  const type = Object.keys(typesObj)[0];
  const color = oneType ? colorMap[type] || jobMarkerColor.delivery : defaultColor;

  if (highlight) {
    return { outer: 'yellow', inner: color.inner, text: 'yellow' };
  }

  return color;
};

export const getJobMarkerIcon = (
  name,
  jobMarkerColor,
  contentOfIcon,
  isHighPriority,
  typesObj,
  isUnassigned,
  highlight,
  highlightUnassigned,
  oneType,
  position,
  jobOrder,
  highlightUnasgnId,
) => {
  switch (name) {
    case 'unassigned-icon':
      return createOrderUnassignedSvg(10, 11, highlightUnassigned || highlightUnasgnId);
    case 'tour-unselected': {
      const color = getColor(typesObj, jobMarkerColor, highlight, oneType, position);
      return createTourUnselectedSvg(color?.inner, color?.outer);
    }
    case 'tour-selected': {
      const color = getColor(typesObj, jobMarkerColor, highlight, oneType, position);
      return createMarkerSvg(
        color.inner,
        color.outer,
        27,
        40,
        contentOfIcon,
        isHighPriority,
        typesObj,
        color.text,
        highlight,
        oneType,
        jobOrder,
      );
    }
    default:
      return '';
  }
};

export const makeJobMarker = (
  feature,
  latlng,
  isSelected,
  onMarkerClick,
  translations,
  hourOffset,
  highlight,
  highlightUnassigned,
  formatDuration,
  isSolution,
  highlightUnassignedId,
) => {
  const {
    properties: {
      arr_time: arrival,
      dep_time: departure,
      jobOrder,
      jobTags,
      routeId,
      types,
      tw,
      address,
      labels,
      highPriority,
      reason,
      territory,
      jobMarkerColor,
      position,
      skills,
      categories,
      priority,
      load,
      isParking,
      parkingDuration,
    },
  } = feature;

  const {
    map: { territoryMarkerTrans },
    wizard: {
      tours: { unassignedReasonsTrans },
    },
  } = translations;
  let highlightUnasgnId = false;
  const isUnassigned = isUnassignedJob(feature);
  if (isUnassigned) {
    highlightUnasgnId = highlightUnassignedId === feature.properties.jobId;
  }

  const typesObj = (types || []).reduce((t, item) => {
    if (!getSafeValue(t, item)) setSafeValue(t, item, 0);
    setSafeValue(t, item, getSafeValue(t, item) + 1);
    return t;
  }, {});

  const iconSize = isUnassigned ? [7, 7] : [10, 10];
  const selectedClass = isSelected
    ? 'tour-selected'
    : isUnassigned
    ? 'unassigned-icon'
    : 'tour-unselected';
  const title = getTitle(translations, labels, typesObj, highPriority, address);
  const reasons = reason ? reason.map((r) => r.description) : null;
  const reasonLabel = reasons && `${unassignedReasonsTrans}: <br/>-&nbsp;${reasons.join('<br/>-')}`;
  const territoryName = territory
    ? `<tr><td>${territoryMarkerTrans}:</td><td>${territory.name}</td></tr>`
    : '';
  const oneType = Object.keys(typesObj).length === 1;
  const iconTypes = [
    'break',
    'reload',
    'drivingRestTime',
    'workingRestTime',
    'pudo',
    'recharge',
    'dutyWorkingRestTime',
    'dutyDrivingRestTime',
  ];
  const showJobOrder = oneType && Object.keys(typesObj).some((key) => iconTypes.includes(key));
  const isPUDO = oneType && Object.keys(typesObj).some((key) => key === 'pudo');
  const contentOfIcon = isSelected && !showJobOrder ? jobOrder : '';

  const parkingDurationElem =
    isParking && parkingDuration !== null && parkingDuration !== undefined && parkingDuration > 0
      ? `<tr><td>${translations.map.parkingDuration}:</td><td>${formatDuration(
          Math.floor(parkingDuration),
        )}</td></tr>`
      : '';

  const options = {
    className: `leaflet-marker-icon-job tour-icon ${selectedClass}`,
    html: L.Util.template(
      getJobMarkerIcon(
        selectedClass,
        jobMarkerColor,
        contentOfIcon,
        highPriority,
        typesObj,
        isUnassigned,
        highlight,
        highlightUnassigned,
        oneType,
        position,
        jobOrder,
        highlightUnasgnId,
      ),
    ),
    iconSize: isSelected ? [35, 35] : iconSize,
    iconAnchor: isSelected ? [12, 35] : [5, 5],
  };

  const m = marker(latlng, {
    icon: new L.DivIcon(options),
  }).bindPopup(
    routeId === undefined
      ? `${title}<br/>${reasonLabel}`
      : createTablePopup(
          translations.map,
          title,
          jobTags,
          arrival,
          departure,
          territoryName,
          '',
          tw,
          hourOffset,
          skills,
          categories,
          position,
          priority,
          '',
          load,
          null,
          showJobOrder && jobOrder,
          isPUDO,
          isSolution,
          true,
          false,
          false,
          null,
          null,
          null,
          null,
          parkingDurationElem,
        ),
    {
      className: `custom-job ${selectedClass}`,
      autoPan: true,
      autoPanPadding: [50, 50],
      keepInView: true,
      maxHeight: 300,
    },
  );

  attachPopupHoverBehavior(m);

  m.on('click', () => {
    if (isMobileDevice()) m.openPopup();
    else onMarkerClick(feature);
  });
  return m;
};

export const makePlanMarker = (
  feature,
  latlng,
  onMarkerClick,
  translations,
  onMarkerDragEnd,
  isSolution,
  asyncOn,
  dispatch,
  orders,
  solution,
) => {
  const {
    global: { pudoLabel },
    map: {
      typeJobDeliveryMarkerTrans,
      typeJobPickupMarkerTrans,
      territoryMarkerTrans,
      skillsMarkerTrans,
      groupMarkerTrans,
      priorityTrans,
    },
    wizard: {
      orders: { editedOrderPriorityHigh },
    },
  } = translations;
  const {
    properties: {
      address,
      labels,
      isEditing,
      highPriority,
      isPickup,
      territory,
      isPudo,
      group,
      skills,
      priority,
      index,
    },
  } = feature;

  const addressLabel = address ? `${address}<br/>` : '';
  const priorityLabel = highPriority
    ? `<div class="high-priority">${editedOrderPriorityHigh}</div>`
    : '';
  const title = labels
    ? `<div class="labels">${priorityLabel}${addressLabel}${labels.join('<br />')}</div>`
    : '';
  const basicIcon = highPriority ? 'plan-icon-high-priority' : 'plan-icon';
  const basicIconClass = isPickup ? `${basicIcon}-pickup` : basicIcon;
  const iconClass = isEditing ? 'plan-editing-icon' : basicIconClass;
  const typeString = isPudo
    ? pudoLabel
    : isPickup
    ? typeJobPickupMarkerTrans
    : typeJobDeliveryMarkerTrans;
  const groupName =
    Array.isArray(group) && group.filter(Boolean).length > 0
      ? `<div>${groupMarkerTrans}: ${group.filter(Boolean).join(', ')}</div>`
      : '';

  const m = marker(latlng, {
    icon: divIcon({
      className: `leaflet-marker-icon-job ${iconClass} id-${index}`,
      html: '<div></div>',
      iconSize: isEditing ? [35, 35] : [24, 24],
      iconAnchor: isEditing ? [12, 35] : [12, 24],
    }),
    draggable: !isSolution && !asyncOn,
  }).bindPopup(
    (el) =>
      createPlanPopup(
        title,
        typeString,
        territoryMarkerTrans,
        groupName,
        el,
        territory,
        skills,
        skillsMarkerTrans,
        priority,
        priorityTrans,
        orders,
      ),
    {
      className: 'custom-job',
      autoPan: true,
      autoPanPadding: [50, 50],
      keepInView: true,
      maxHeight: 300,
    },
  );

  attachPopupHoverBehavior(m);

  m.on('contextmenu', () => {
    const featureId = feature.properties.id;
    const newOrders = orders.filter((o) => o.InternalID !== featureId);
    dispatch(setOrders(newOrders, false, solution.show));
    dispatch(setSolutionJson({ jsonTriggerVRP: true }));
  });
  m.on('click', () => {
    if (isMobileDevice()) m.openPopup();
    else onMarkerClick(feature, true);
  });
  m.on('dragend', () => {
    onMarkerDragEnd(m, feature);
  });
  return m;
};

export const makeActivityMarker = (
  feature,
  latlng,
  translations,
  hourOffset,
  onMarkerClick,
  highlight,
  formatDuration,
  recharges,
  isStopSelected,
) => {
  const {
    properties: {
      types,
      jobMarkerColor,
      arr_time: arrival,
      dep_time: departure,
      jobTags,
      tw,
      address,
      labels,
      highPriority,
      territory,
      stopTerritory,
      group,
      position,
      skills,
      categories,
      priority,
      load,
      distance,
      houseKeyId,
      maxTime,
      stopIndex,
      tourIndex,
      isParking,
      parkingDuration,
      activityOrder,
    },
  } = feature;

  const {
    map: { territoryMarkerTrans, groupMarkerTrans },
  } = translations;

  const typesObj = (types || []).reduce((t, item) => {
    if (!getSafeValue(t, item)) setSafeValue(t, item, 0);
    setSafeValue(t, item, getSafeValue(t, item) + 1);
    return t;
  }, {});

  if (isParking) {
    setSafeValue(typesObj, 'parking', 1);
  }

  const typesClass = typesObj.pickup ? 'tour-icon-pickup' : 'tour-icon-delivery';
  const iconTypes = [
    'break',
    'reload',
    'drivingRestTime',
    'workingRestTime',
    'pudo',
    'recharge',
    'parking',
    'dutyWorkingRestTime',
    'dutyDrivingRestTime',
  ];
  const oneType = Object.keys(typesObj).length === 1;
  const showDurationAndGroup =
    oneType && Object.keys(typesObj).some((key) => iconTypes.includes(key));
  const duration = moment(departure).diff(moment(arrival), 'minutes');
  const formattedDuration = duration > 0 ? formatDuration(Math.floor(duration)) : 'Unknown';
  const isPUDO = oneType && Object.keys(typesObj).some((key) => key === 'pudo');
  const isRecharge = oneType && Object.keys(typesObj).some((key) => key === 'recharge');
  const isParkingMark = oneType && Object.keys(typesObj).some((key) => key === 'parking');
  const isActivityAtParkingStop = isParking && !isParkingMark;

  const highlightColor = {
    outer: 'yellow',
    inner:
      typesClass === 'tour-icon-pickup'
        ? jobMarkerColor.pickup.inner
        : jobMarkerColor.delivery.inner,
    text:
      typesClass === 'tour-icon-pickup' ? jobMarkerColor.pickup.text : jobMarkerColor.delivery.text,
  };

  const color = highlight
    ? highlightColor
    : typesClass === 'tour-icon-pickup'
    ? jobMarkerColor.pickup
    : jobMarkerColor.delivery;

  const showOrderNumber = isStopSelected && activityOrder > 0;
  const orderNumberText = showOrderNumber ? String(activityOrder) : '';
  const useMarkerPin = isParkingMark || isRecharge || isActivityAtParkingStop;

  const options = {
    className: `leaflet-marker-icon-job tour-icon`,
    html: L.Util.template(
      useMarkerPin
        ? createMarkerSvg(
            color.inner,
            color.outer,
            isParkingMark || isRecharge ? 27 : 22,
            isParkingMark || isRecharge ? 40 : 32,
            isParkingMark || isRecharge ? '' : String(stopIndex),
            false,
            typesObj,
            color.text,
            highlight,
            oneType,
            stopIndex,
          )
        : showOrderNumber
        ? createMarkerSvg(
            color.inner,
            color.outer,
            20,
            30,
            orderNumberText,
            false,
            typesObj,
            '#fff',
            highlight,
            oneType,
            stopIndex,
          )
        : createTourUnselectedSvg(color.inner, color.outer, highlight, true, stopIndex),
    ),
    iconSize:
      isParkingMark || isRecharge
        ? [35, 35]
        : useMarkerPin
        ? [22, 32]
        : showOrderNumber
        ? [20, 30]
        : [10, 10],
    iconAnchor:
      isParkingMark || isRecharge
        ? [12, 35]
        : useMarkerPin
        ? [11, 32]
        : showOrderNumber
        ? [10, 15]
        : [5, 9],
  };

  const title = getTitle(translations, labels, typesObj, highPriority, address);

  const territoryName = territory
    ? `<tr><td>${territoryMarkerTrans}:</td><td>${territory.name}</td></tr>`
    : '';
  const stopTerritoryName = stopTerritory
    ? `<tr><td>${territoryMarkerTrans}:</td><td>${stopTerritory?.name}</td></tr>`
    : '';
  const durationElem =
    formattedDuration !== 'Unknown'
      ? `<tr><td>${translations.map.duration}:</td><td>${formattedDuration}</td></tr>`
      : '';

  const parkingDurationElem =
    isParking && parkingDuration !== null && parkingDuration !== undefined && parkingDuration > 0
      ? `<tr><td>${translations.map.parkingDuration}:</td><td>${formatDuration(
          Math.floor(parkingDuration),
        )}</td></tr>`
      : '';

  const groupName = group ? `<tr><td>${groupMarkerTrans}:</td><td>${group.name}</td></tr>` : '';
  const tourRecharge = recharges[tourIndex];
  const rechargeIdx = distance
    ? tourRecharge.findIndex((recharge) => recharge === distance.distance)
    : null;
  let rechargeDistance;

  if (distance?.distance >= 0 && rechargeIdx >= 0 && isRecharge) {
    if (rechargeIdx === 0) rechargeDistance = tourRecharge[0];
    else rechargeDistance = tourRecharge[rechargeIdx] - tourRecharge[rechargeIdx - 1];
  }

  const m = marker(latlng, {
    icon: new L.DivIcon(options),
    zIndexOffset: isParkingMark ? 1000 : 0,
  }).bindPopup(
    (el) =>
      createTablePopup(
        translations.map,
        title,
        jobTags,
        arrival,
        departure,
        territoryName,
        groupName,
        tw,
        hourOffset,
        skills,
        categories,
        position,
        priority,
        durationElem,
        load,
        Math.abs(rechargeDistance),
        null,
        isPUDO,
        false,
        false,
        showDurationAndGroup,
        true,
        el.feature.properties.jobIds[0],
        stopTerritoryName,
        houseKeyId,
        maxTime,
        parkingDurationElem,
      ),
    {
      className: 'custom-job tour-selected',
      autoPan: true,
      autoPanPadding: [50, 50],
      keepInView: true,
      maxHeight: 300,
    },
  );

  attachPopupHoverBehavior(m);

  m.on('click', () => {
    if (isMobileDevice()) m.openPopup();
    else onMarkerClick(feature);
  });

  return m;
};

export function rgbToHex(color) {
  const rgbColor = color
    .substring(4, color.length - 1)
    .replace(/ /g, '')
    .split(',');
  const r = Math.min(255, Math.max(0, rgbColor[0]));
  const g = Math.min(255, Math.max(0, rgbColor[1]));
  const b = Math.min(255, Math.max(0, rgbColor[2]));

  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase()}`;
}

export const makeUndesiredMarker = (feature, latlng) => {
  const options = {
    className: `leaflet-marker-icon-job tour-icon`,
    html: createUndesiredPointSvg(rgbToHex(feature.properties.colorGradient)),
    iconSize: [10, 10],
    iconAnchor: [5, 9],
    style: 'background-color: transparent;',
  };

  const m = marker(latlng, {
    icon: new L.DivIcon(options),
  }).bindPopup((el) => createUndesiredPopup(feature.properties.count, el), {
    className: 'custom-job tour-selected',
    autoPan: true,
    autoPanPadding: [50, 50],
    keepInView: true,
    maxHeight: 300,
  });

  attachPopupHoverBehavior(m);

  m.on('click', () => {
    m.openPopup();

    // To be discussed
    // if (isMobileDevice()) m.openPopup();
    // else onMarkerClick(feature);
  });

  return m;
};

export const makeMarker = (
  feature,
  latlng,
  display,
  onMarkerClick,
  translations,
  onMarkerDragEnd,
  isSolution,
  hourOffset,
  highlight,
  highlightUnassigned,
  asyncOn,
  dispatch,
  orders,
  solution,
  formatDuration,
  recharges,
  highlightUnassignedId,
) => {
  if (isUndesiredPoint(feature))
    return makeUndesiredMarker(
      feature,
      latlng,
      translations,
      hourOffset,
      onMarkerClick,
      highlight,
      formatDuration,
      recharges,
    );
  if (isActivityToDisplay(feature)) {
    return makeActivityMarker(
      feature,
      latlng,
      translations,
      hourOffset,
      onMarkerClick,
      highlight,
      formatDuration,
      recharges,
      highlight,
    );
  }
  if (isPointDepot(feature))
    return makeDepotMarker(
      feature,
      latlng,
      onMarkerClick,
      translations,
      onMarkerDragEnd,
      isSolution,
      highlight,
      asyncOn,
    );
  if (isPointReturnLocation(feature))
    return makeReturnLocationMarker(
      feature,
      latlng,
      onMarkerClick,
      translations,
      onMarkerDragEnd,
      isSolution,
      asyncOn,
    );
  if (isRouteToDisplay(feature, display))
    return makeJobMarker(
      feature,
      latlng,
      true,
      onMarkerClick,
      translations,
      hourOffset,
      highlight,
      highlightUnassigned,
      formatDuration,
      isSolution,
      highlightUnassignedId,
    );
  return !display.showProblem
    ? makeJobMarker(
        feature,
        latlng,
        false,
        onMarkerClick,
        translations,
        hourOffset,
        highlight,
        highlightUnassigned,
        formatDuration,
        isSolution,
        highlightUnassignedId,
      )
    : makePlanMarker(
        feature,
        latlng,
        onMarkerClick,
        translations,
        onMarkerDragEnd,
        isSolution,
        asyncOn,
        dispatch,
        orders,
        solution,
      );
};
